// delivery-estimate.js
class DeliveryEstimate extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._weight = null;
    this.token = "";
  }

  static get observedAttributes() {
    return ['loading', 'weight'];
  }

  get weight() {
    return this._weight;
  }

  /**
   * Setter for product weight (in grams)
   */
  set weight(value) {
    this._weight = value;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();

    // Parse initial weight attribute if present
    const weightAttr = this.getAttribute('weight');
    if (weightAttr) {
      this.weight = weightAttr;
    }    

    // store token
    this.token = this.getAttribute('data-token');
  
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'loading') {
      const button = this.shadowRoot.querySelector('button');
      if (button) {
        button.disabled = newValue === 'true';
      }
    } else if (name === 'weight' && newValue !== oldValue) {
      this.weight = newValue;
    }
  }

  async fetchDeliveryEstimate(pincode) {
    try {
      const hdrs = new Headers();
      hdrs.append("Content-Type", "application/json");
      hdrs.append("Authorization", `Bearer ${this.token}`);

      const requestOptions = {
        method: "GET",
        headers: hdrs,
        redirect: "follow"
      };

      const weightInKgs = this.weight ? this.weight / 1000 : 20; // Default weight is 20kg
      const response = await fetch(`https://apiv2.shiprocket.in/v1/external/courier/serviceability/?pickup_postcode=421302&delivery_postcode=${pincode}&cod=0&weight=${weightInKgs}`, requestOptions);

      if (!response.ok) throw new Error('Unable to fetch delivery estimate');

      const resp_json = await response.json();
      const edd = this.processShipRocketRespForEdd(resp_json);
      return edd;
    } catch (error) {
      throw new Error('Unable to fetch delivery estimate');
    }
  }

  /**
   * 
   * @param {*} resp_json 
   * @returns {estimated_delivery_days: number, status: string, message: string}
   */
  processShipRocketRespForEdd(resp_json) {
    if(resp_json.status !== 200){
      return resp_json;
    }

    const preferedCourierSubStr = "Delhivery Surface";
    const days = resp_json?.data?.available_courier_companies?.filter(c=>c.courier_name.includes(preferedCourierSubStr))[0]["estimated_delivery_days"];
    // TODO: Handle case when prefered courier is not available
    return {estimated_delivery_days: days, status: 200, message: 'Success'};
  }

  setupEventListeners() {
    const input = this.shadowRoot.querySelector('input');
    const button = this.shadowRoot.querySelector('button');
    const errorText = this.shadowRoot.querySelector('.error-text');

    input.addEventListener('input', (e) => {
      // Only allow numbers
      e.target.value = e.target.value.replace(/[^0-9]/g, '');
      // Limit to 6 digits (Indian pincode length)
      if (e.target.value.length > 6) {
        e.target.value = e.target.value.slice(0, 6);
      }
      this.setDefaultHint();
    });

    button.addEventListener('click', async () => {
      const pincode = input.value;
      if (pincode.length !== 6) {
        this.setErrorHint('Please enter a valid 6-digit pincode');
        return;
      }

      try {
        this.setAttribute('loading', 'true');
        const estimate = await this.fetchDeliveryEstimate(pincode);
        if(estimate.status !== 200){
          if(estimate.status === 404){
            this.setErrorHint(`No courier service available for ${pincode}`);
            return;
          }
          this.setErrorHint(estimate.message);
          return;
        }
        const edd = estimate.estimated_delivery_days;
        const now = new Date();
        const bestDate = now.addDays(Number(edd)-2);
        const bestDateFormatted = bestDate.toLocaleString('en-US', { month: 'short', day: '2-digit' });
        const worstDate = now.addDays(Number(edd));
        const worstDateFormatted = worstDate.toLocaleString('en-US', { month: 'short', day: '2-digit' });

        const innerHTML =
        `Expected between <span class="edd-date">${bestDateFormatted}</span> and <span class="edd-date">${worstDateFormatted}</span>`;
        this.setEstimateHint(innerHTML);
      } catch (error) {
        this.setErrorHint(error.message);
      } finally {
        this.setAttribute('loading', 'false');
      }
    });
  }

  setDefaultHint(){
    const hintTextElem = this.shadowRoot.querySelector('.estimate-hint');
    if(hintTextElem) {
      hintTextElem.textContent = '';
      hintTextElem.classList.remove('show');
    }
    
    const errTextElem = this.shadowRoot.querySelector('.error-text');
    if(errTextElem) {
      errTextElem.textContent = '';
      errTextElem.classList.remove('show');
    }
    
    const defaultTextElem = this.shadowRoot.querySelector('.default-hint');
    if(defaultTextElem) {
      defaultTextElem.classList.add('show');
    }
  }

  setErrorHint(text){
    const hintTextElem = this.shadowRoot.querySelector('.estimate-hint');
    if(hintTextElem) {
      hintTextElem.textContent = '';
      hintTextElem.classList.remove('show');
    }

    const defaultTextElem = this.shadowRoot.querySelector('.default-hint');
    if(defaultTextElem) {
      defaultTextElem.classList.remove('show');
    }
    
    const errTextElem = this.shadowRoot.querySelector('.error-text');
    if(errTextElem) {
      errTextElem.textContent = text;
      errTextElem.classList.add('show');
    }
  }

  setEstimateHint(innerHTML){
    const errTextElem = this.shadowRoot.querySelector('.error-text');
    if(errTextElem) {
      errTextElem.textContent = '';
      errTextElem.classList.remove('show');
    }

    const defaultTextElem = this.shadowRoot.querySelector('.default-hint');
    if(defaultTextElem) {
      defaultTextElem.classList.remove('show');
    }

    const hintTextElem = this.shadowRoot.querySelector('.estimate-hint');
    if(hintTextElem) {
      hintTextElem.innerHTML = innerHTML;
      hintTextElem.classList.add('show');
    }
  }

  render() {
    const style = `
      :host {
        display: block;
        font-family: system-ui, -apple-system, sans-serif;
      }

      .container {
        padding: 8px 12px;
        background: rgba(12, 12, 12, 0.32);
        border-radius: 8px;
        display: flex;
        gap: 12px;
        align-items: center;
        color: #bfbfbf;
      }
      @media screen and (orientation: portrait) and (max-width: 1081px) {
        .container {
          margin-inline: -10px;
          padding: 8px 10px;
          background: rgba(12, 12, 12, 0.48);
          gap: 8px;
        } 
      }

      .title {
        font-size: 12px;
        font-weight: 600;
        line-height: 140%;
      }

      .separator {
        height: 24px;
        width: 1px;
        background-color: rgba(255, 255, 255, 0.16);
      }

      .main-area {
        flex-grow: 1;
      }

      .input-group {
        gap: 8px;
        margin-bottom: 6px;
        position: relative;
        display: flex;
      }

      input {
        height: 28px;
        border: none;
        border-radius: 14px;
        padding: 0 0 0 10px;
        background-color: rgba(255,255,255,10%) !important;
        caret-color: rgba(255,255,255,60%);
        color: white !important;
        font-size: 12px;
        flex-grow: 1;
        appearance: none;
        -moz-appearance: textfield;
        font-family: 'Metropolis';
      }

      input:-webkit-autofill,
      input:-webkit-autofill:focus {
        transition: background-color 0s 600000s, color 0s 600000s !important;
      }

      input:focus {
        outline: none;
      }

      input.error {
        outline: 0.8px solid #EF4D22;
      }

      input::placeholder {
        color: #888;
      }

      input::-webkit-outer-spin-button,input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      @media screen and (orientation: portrait) and (max-width: 1081px) {
        input {
          height: 36px;
          border-radius: 18px;
        }
      }

      button {
        width: 76px;
        height: 28px;
        color: #fff;
        background: linear-gradient(102.55deg, rgba(255, 255, 255, 0.1) 13.1%, rgba(255, 255, 255, 0.2) 86.9%);
        border-radius: 17px;
        cursor: pointer;
        font-family: 'Metropolis';
        font-size: 12px;
        font-weight: 500;
        border: none;
        position: absolute;
        right: 0;
      }

      button:hover {
        opacity: 0.9;
      }

      button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }

      @media screen and (orientation: portrait) and (max-width: 1081px) {
        button {
          height: 36px;
          border-radius: 18px;
        }
      }
      
      .hint-text-container {
        font-size: 12px;
        line-height: 120%;
        padding-left: 10px;
      }
      @media screen and (orientation: portrait) and (max-width: 1081px) {
        .hint-text-container {
          font-size: 10px;
        }
      }

      .default-hint {
        display: none;
      }

      .error-text {
        color: #EF4D22;
        display: none;
      }

      .show {
        display: unset;
      }

      .estimate-hint .edd-date{
        font-weight: 600;
        color: #fff;
      }
      @media screen and (orientation: portrait) and (max-width: 1081px) {
        .estimate-hint .edd-date {
          font-weight: 700;
        }
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${style}</style>
      <div class="container">

        <div class="title">Delivery<br>Estimate</div>

        <div class="separator"></div>

        <div class="main-area">
          <div class="input-group">
            <input type="number" placeholder="Enter pincode" maxlength="6" />
            <button>Check</button>
          </div>

          <div class="hint-text-container">
            <div class="error-text"></div>
            <div class="default-hint show">Delivery within 3-7 days across India</div>
            <div class="estimate-hint"></div>
          </div>
        </div>

      </div>
    `;
  }
}

customElements.define('delivery-estimate', DeliveryEstimate);